import { featureFlags } from '@config/featureFlags';
import { cn } from '@lib/utils';
import type { EventRead } from '@models/api';
import { formatEventWhen } from '@utils/datetime';
import { formatStartsIn, type LiveEventPhase } from '@utils/eventLiveness';
import { resolveEventPhaseBadgeKind } from './EventPhaseBadge';
import { STARTING_SOON_LABEL } from './LiveStateChip';

export interface SlotCardChipProps {
  /** The event's window plus what decides its display zone (location, timezone). */
  event: Pick<EventRead, 'start_date' | 'end_date'> &
    Partial<Pick<EventRead, 'location' | 'timezone'>>;
  /** The host's clock (the slot's `useNow`); the chip mounts NO timer of its own. */
  now: Date;
  /**
   * Pre-derived phase from the host (`pickSlotCells` already derived it for the
   * cell), threaded so the chip does NOT recompute `getEventPhase` on every tick.
   * Optional — omitted, the shared resolver derives it from `event` + `now`.
   */
  phase?: LiveEventPhase;
}

/**
 * The Figma on-image chip shell (6940:18935): a grey `#f0f0f0` pill, ~10px Fira,
 * a small leading dot. `px`/`py`/`gap`/`text`/the dot's `size` are the node's
 * exact sub-pixel Figma export values (2.9px, 7.25px, 10.15px...) — reviewed for
 * the 2026-07-23 Tailwind-scale sweep and kept arbitrary on purpose: no standard
 * step sits close enough to round to without visibly drifting a pill this small,
 * and they're one interdependent, co-calibrated set (rounding only some of them
 * would throw off the others' proportions). The dot uses the card's `mb-teal`
 * accent.
 */
const SLOT_CHIP_BASE =
  'inline-flex w-fit shrink-0 items-center gap-[2.9px] rounded-full px-[7.25px] py-[2.9px] text-[10.15px] leading-none whitespace-nowrap';
const SLOT_CHIP_DOT = 'size-[7px] shrink-0 rounded-full';

/**
 * Grey countdown / "Starting soon" / date chip — Figma dark-teal `#1d5663` on
 * `#f0f0f0`. Also reviewed for the Tailwind-scale sweep and kept literal: no
 * existing token is an exact match (default `gray-100`/`neutral-100` are both
 * slightly off #f0f0f0, and the app's own `mb-bg` #f0efeb is a warm cream token
 * semantically tied to the page background, not a neutral chip grey — swapping
 * either in would shift this Figma-spec'd color, not merely rename it).
 */
function SlotGreyChip({ label }: { label: string }) {
  return (
    <span className={cn(SLOT_CHIP_BASE, 'bg-[#f0f0f0] font-normal text-[#1d5663]')}>
      <span aria-hidden="true" className={cn(SLOT_CHIP_DOT, 'bg-mb-teal')} />
      {label}
    </span>
  );
}

/**
 * Live chip — the SAME grey `#f0f0f0` pill family as the other tiers, with red
 * `mb-live` text + dot (W2.6 carry-over fix, Dexter's Figma confirmation
 * 2026-07-23): the card's chips are ONE family now, so the live cue is the family
 * pill in red rather than the app-wide white `LiveBadge`. The dot is STATIC (no
 * pulse) — it matches the soon chip's static dot for family unity, and emil's
 * frequency rule argues against animating a chip seen on every live card;
 * `LiveBadge` keeps its pulse everywhere else. The red-dot-plus-"Live" pair is
 * still the L9-reserved liveness signal, only restyled to the card family.
 */
function SlotLiveChip() {
  return (
    <span className={cn(SLOT_CHIP_BASE, 'bg-[#f0f0f0] font-semibold text-mb-live')}>
      <span aria-hidden="true" className={cn(SLOT_CHIP_DOT, 'bg-mb-live')} />
      Live
    </span>
  );
}

/**
 * The space-slot card's on-image "when" badge (Figma 6940:18935 / 6940:18338,
 * W2.7). The UNIVERSAL when-cue: every rail card wears exactly one chip. It
 * CONSUMES the ONE shared badge ladder — `resolveEventPhaseBadgeKind`, the same
 * resolver `EventPhaseBadge` renders from — as its `card-chip` variant, then maps
 * the semantic kind to THIS card's grey/red visual treatment (with the locked copy
 * `formatStartsIn` / `STARTING_SOON_LABEL` / `formatEventWhen`). No phase or band
 * branching is duplicated here, so a ladder change (a new tier, the starting-soon
 * gating rule) lands in one place and this chip can never drift from the badge.
 * Four tiers (Dexter's ruling 2026-07-23):
 *   - live (kind `live`)                    → the grey/red Live chip (L9 pair, card family)
 *   - soon (kind `soon`)                    → grey chip with the locked `formatStartsIn` countdown
 *   - within the 6h band (kind `starting-soon`) → grey chip with `STARTING_SOON_LABEL`
 *   - beyond it (kind `date`, card-chip only)   → grey chip with the compact `formatEventWhen`
 *
 * The teal "Going" chip RETIRED from the ladder (W2.7): row 3's "You're going"
 * status line owns that meaning now, so this chip is purely a when-cue and needs
 * no going state. Flag-gated by the same `liveEvents` kill switch as
 * `EventPhaseBadge`, so the host may render it unconditionally.
 */
export function SlotCardChip({ event, now, phase }: SlotCardChipProps) {
  if (!featureFlags.liveEvents) {
    return null;
  }
  switch (resolveEventPhaseBadgeKind(event, now, 'card-chip', phase)) {
    case 'live':
      return <SlotLiveChip />;
    case 'soon': {
      const label = formatStartsIn(event, now);
      return label ? <SlotGreyChip label={label} /> : null;
    }
    case 'starting-soon':
      return <SlotGreyChip label={STARTING_SOON_LABEL} />;
    case 'date': {
      // `now` is the host's clock, so "Today"/"Tomorrow" tick with the slot.
      // Viewer frame: this chip is a list surface (design.md, "Event lists use
      // the viewer's timezone").
      const { date } = formatEventWhen(event, now, 'viewer');
      return date ? <SlotGreyChip label={date} /> : null;
    }
    // ended / none — rail cells never reach here (the recap cell uses EventPhaseBadge).
    default:
      return null;
  }
}
